import { FEATURE_FLAGS } from "./types/api";
import type { IToolMeta } from "./types/mongo/client/report";
import { getHost } from "./utils/etc";

export const TOOL_KEYS = [
    "account_settings",
    "affordability",
    "amortization",
    "appreciation",
    "arm_vs_fixed",
    "barrys_insights",
    "bid_over_ask",
    "blended_rate",
    "buy_vs_rent",
    "buying_power",
    "client_manager",
    "cost_of_waiting",
    "debt_consolidation",
    "econ_calendar",
    "equity_gained",
    "homepage",
    "house_price_index",
    "investment_property_analyzer",
    "loan_compare",
    "market_wrap",
    "marketing_kit",
    "markets",
    "mauldin_tftf",
    "mbestimate",
    "meetings",
    "morning_update",
    "newsletter",
    "open_house_flyers",
    "payment",
    "presentation_expressway",
    "purchase_analysis",
    "recordings",
    "referral_terms",
    "refi_cow_express",
    "reinvestment",
    "real_estate_report_card",
    "seller_contribution",
    "social_share",
    "social_studio",
    "strike_rate",
    "user",
    "agent_feed",
    "week_on_highway",
    "avm_widget",
    "formations_and_completions",
    "home_values",
] as const;

export const TOOL_VARIANT_KEYS = [
    "loan_compare_purchase",
    "loan_compare_refinance",
    "debt_consolidation_refinance",
    "debt_consolidation_moveup",
    "debt_consolidation_purchase",
] as const;

export const TOOLS_KEYS_WITH_VARIANTS = [...TOOL_KEYS, ...TOOL_VARIANT_KEYS];

export type TToolKey = (typeof TOOL_KEYS)[number];
export type TToolVariantKeys = (typeof TOOL_VARIANT_KEYS)[number];
export type TToolKeysWithVariants = (typeof TOOLS_KEYS_WITH_VARIANTS)[number];

export type TReportCategory = "purchase" | "refinance" | "invest";

export type TToolCategory = "loan-advisor" | "real-estate" | "calculators" | "marketing" | "user" | "markets";

type TToolVariant = {
    label: string;
    params?: Record<any, any>;
    /** Used to build a unique id for REPORT_OPTIONS (e.g. "lc2-purchase") */
    variantKey: TToolVariantKeys;
    /** Path suffix appended to the base url path (e.g. "?type=purchase") */
    pathSuffix?: string;
    /** Override categories for this variant */
    categories?: TReportCategory[];
    /** Override thumbnail for this variant */
    thumbnail?: string | string[];
    /** Feature flag key that must be enabled for this variant to be visible */
    featureFlag?: keyof typeof FEATURE_FLAGS;
};

//NOTE - When updating this, also update etc.ts:TToolNames in the express-service
export type TTool =
    | "lc2"
    | "cow"
    | "dc"
    | "rerc"
    | "boa"
    | "bvr"
    | "appr"
    | "blend"
    | "amort"
    | "record"
    | "meetings"
    | "mrkt"
    | "home"
    | "hpi"
    | "pa"
    | "news"
    | "pay"
    | "cm"
    | "reinvest"
    | "rce"
    | "eg"
    | "ssh"
    | "sst"
    | "mkit"
    | "housing"
    | "impact"
    | "mrktw"
    | "mu"
    | "bpow"
    | "presentation"
    | "econcal"
    | "tftf"
    | "bi"
    | "str"
    | "avf"
    | "accs"
    | "sc"
    | "ipa"
    | "mbe"
    | "aff"
    | "ohf"
    | "agent_feed"
    | "woh"
    | "avm";

export type TField =
    | "property"
    | "existingProperty"
    | "loan"
    | "purchaseLoan"
    | "selectedLoan"
    | "existingLoan"
    | "refiLoan"
    | "cowLoan"
    | "attachedReports"
    | "debt"
    | "campaignSettings";

export enum EAddonType {
    LOAN_ADVISOR = "Loan Advisor",
    MARKETING_KIT = "90 Day Kit",
    HOME_REPORT = "HomeReport 2.0",
    /**
     * The free HomeReport tier. Named to match the `membershipAddOns.maoName` the legacy
     * checkAddon resolved against, which is also the Portal planId the session carries.
     */
    HOME_REPORT_FREEMIUM = "Home Report Freemium LO",
    PRESENTATION_EXPRESSWAY = "Presentation Expressway",
    SOCIAL_STUDIO = "Social Studio",
    SCREEN_RECORDING = "Screen Recording",
    OPEN_HOUSE_KIT = "Open House Kit",
}

export const ADDON_PAGE_URLS = {
    [EAddonType.LOAN_ADVISOR]: "https://www.highway.ai/addon/loan-advisor",
    [EAddonType.MARKETING_KIT]: "https://www.highway.ai/addon/marketing-kit",
    [EAddonType.PRESENTATION_EXPRESSWAY]: "https://www.highway.ai/addon/presentation-expressway",
    [EAddonType.SOCIAL_STUDIO]: "https://www.highway.ai/addon/social-studio",
} as const;

export interface ITool {
    name: string;
    version?: string;
    /** @deprecated Use toolKey instead */
    tool: TTool;
    toolKey?: TToolKey;
    url: string;
    /** URL for tools that haven't been migrated to the SPA yet */
    legacyUrl?: string;
    category?: TToolCategory;
    variants?: TToolVariant[];
    customMessages?: (args?: any) => { default: string; video: string };
    /** We should deprecate these in favor of toolKey */
    eventPrefix?: string;
    requiredFields?: (type?: IToolMeta["type"]) => Partial<Record<TField, number>>;
    requiredAddon?: EAddonType;
    addonPageUrl?: string;
    pages?: { results: boolean; review: boolean };
    /** If the page hasn't been modernized yet */
    isLegacy?: boolean;
    /** Preview thumbnail image URL(s) — array enables multi-page gallery */
    thumbnail?: string | string[];
    /** Short description shown in the Create Report drawer */
    description?: string;
    /** Categories this tool belongs to in the Create Report drawer */
    categories?: TReportCategory[];
    /** Feature flag key that must be enabled for this tool to be visible */
    featureFlag?: keyof typeof FEATURE_FLAGS;
    /** If we want to hide the ability to favorite the tool */
    notFavoritable?: boolean;
}

export const NEXT_PUBLIC_URL = process.env["NEXT_PUBLIC_PUBLIC_URL"] || "/hwapp/app/";

export const TOOLS: ITool[] = [
    {
        name: "Purchase Analysis",
        tool: "pa",
        toolKey: "purchase_analysis",
        version: "999.0",
        url: "/members/purchase-analysis",
        thumbnail: "https://mbshighway.com/s3/images/report-demos/purchase-analysis.jpg",
        category: "loan-advisor",
        description:
            "Share Loan Comparison, Debt Consolidation and Buy vs Rent with buyers so they can analyze payment differences, net worth creation and more with your guidance.",
        categories: ["purchase"],
        customMessages: () => ({
            default: `I wanted to send over a comprehensive Purchase Analysis for you, which includes a personalized loan comparison report, cost of waiting analysis, as well as the financial implications of buying vs renting and more. Please let me know if you have any questions - I'm happy to set up a time to chat whenever it is convenient.`,
            video: `I made you a short video reviewing a comprehensive Purchase Analysis for you, which includes a personalized loan comparison report, cost of waiting analysis, as well as the financial implications of buying vs renting and more. Please let me know if you have any questions - I'm happy to set up a time to chat whenever it is convenient.`,
        }),
        eventPrefix: "purchase_analysis",
        requiredFields: () => ({
            property: 1,
            loan: 1,
            selectedLoan: 1,
            attachedReports: 3,
        }),
        requiredAddon: EAddonType.LOAN_ADVISOR,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.LOAN_ADVISOR],
        pages: { results: true, review: false },
    },
    {
        name: "Client Manager",
        tool: "cm",
        toolKey: "client_manager",
        version: "999.9",
        url: "/members/client-manager",
        category: "user",
        customMessages: () => ({
            default: ``,
            video: ``,
        }),
    },
    {
        name: "Loan Comparison",
        tool: "lc2",
        toolKey: "loan_compare",
        version: "1.0",
        url: "/members/loan-compare",
        description:
            "Easily compare different loan options for your clients, including costs for each loan, breakdowns of each payment, and the difference due at closing depending on the number of years they plan on living there.",
        category: "loan-advisor",
        variants: [
            {
                label: "Purchase",
                variantKey: "loan_compare_purchase",
                pathSuffix: "?type=purchase",
                categories: ["purchase"],
                thumbnail: [1, 2, 3, 4, 5].map(
                    (n) => `https://mbshighway.com/s3/images/report-demos/lc-purchase-${n}.png`,
                ),
            },
            {
                label: "Refinance",
                variantKey: "loan_compare_refinance",
                pathSuffix: "?type=refi",
                categories: ["refinance"],
                thumbnail: [1, 2, 3, 4].map(
                    (n) => `https://mbshighway.com/s3/images/report-demos/lc-refi-${n}.png`,
                ),
            },
        ],
        customMessages: () => ({
            default:
                "I wanted to send over this side-by-side loan comparison that highlights the different financing options available to you. Let me know if you have any questions or would like to chat more to ensure we find the best fit for you.",
            video: "I made a quick video explaining the different loan options that are available to you, including a side-by-side comparison and the benefits of each scenario. Let me know if you have any questions or would like to chat in more detail soon!",
        }),
        requiredFields: (type) =>
            type === "purchase"
                ? {
                      property: 1,
                      purchaseLoan: 1,
                  }
                : {
                      existingProperty: 1,
                      existingLoan: 1,
                      refiLoan: 1,
                  },
        requiredAddon: EAddonType.LOAN_ADVISOR,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.LOAN_ADVISOR],
        pages: { results: true, review: true },
    },
    {
        name: "Cost of Waiting",
        tool: "cow",
        toolKey: "cost_of_waiting",
        version: "1.7",
        url: "/members/cost-of-waiting",
        thumbnail: "https://mbshighway.com/s3/images/report-demos/cost-of-waiting.png",
        description:
            "Calculate the missed financial opportunity when delaying a home purchase by considering monthly payment, forecasted appreciation gains, and more.",
        categories: ["purchase"],
        category: "loan-advisor",
        customMessages: () => ({
            default:
                "At current home prices and mortgage rates, the Cost of Acting can feel very high. But with home prices rising, the Cost of Waiting is even higher! Here's a Cost of Waiting analysis for your area to help you understand how appreciation, amortization, and payments are affected by delaying a home purchase.",
            video: "At current home prices and mortgage rates, the Cost of Acting can feel very high. But with home prices rising, the Cost of Waiting is even higher! I made a quick video walking you through a Cost of Waiting analysis for your area to help you understand how appreciation, amortization, and payments are affected by delaying a home purchase. Let me know if you have any questions — I'm here to chat any time.",
        }),
        eventPrefix: "cow",
        requiredFields: () => ({
            property: 1,
            loan: 1,
        }),
        requiredAddon: EAddonType.LOAN_ADVISOR,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.LOAN_ADVISOR],
        pages: { results: true, review: true },
    },
    {
        name: "Debt Consolidation",
        tool: "dc",
        toolKey: "debt_consolidation",
        version: "1.5",
        url: "/members/debt-consolidation",
        category: "loan-advisor",
        description:
            "Show your clients how they can accelerate payoff of mortgage, finance education, and set-up retirement while keeping existing payment at the same level.",
        variants: [
            {
                label: "Move-Up Purchase",
                variantKey: "debt_consolidation_moveup",
                pathSuffix: "?type=moveup",
                categories: ["purchase"],
                thumbnail: ["", "-2", "-3"].map(
                    (n) =>
                        `https://mbshighway.com/s3/images/report-demos/debt-consolidation-purchase${n}.png`,
                ),
            },
            {
                label: "Refinance",
                variantKey: "debt_consolidation_refinance",
                pathSuffix: "?type=refi",
                categories: ["refinance"],
                thumbnail: ["", "-2", "-3"].map(
                    (n) =>
                        `https://mbshighway.com/s3/images/report-demos/debt-consolidation-refinance${n}.png`,
                ),
            },
            {
                label: "Purchase",
                variantKey: "debt_consolidation_purchase",
                pathSuffix: "?type=purchase",
                featureFlag: "dcPurchase",
                categories: ["purchase"],
                thumbnail: ["-1", "-2", "-3"].map(
                    (n) =>
                        `https://mbshighway.com/s3/images/report-demos/debt-consolidation-purchase_nmu${n}.png`,
                ),
            },
        ],
        customMessages: () => ({
            default:
                "I wanted to send over your debt consolidation scenario, which illustrates how you can lower your overall monthly financial obligation by restructuring some of your debt, so you're in a better position to build wealth for your family. Let me know if you have any questions — I'm here to chat any time.",
            video: "I made a quick video walking you through your debt consolidation scenario. This tool illustrates how you can lower your overall monthly financial obligation by restructuring some of your debt, so you're in a better position to build wealth for your family. Let me know if you have any questions — I'm here to chat any time.",
        }),
        requiredFields: (type) => {
            if (type === "refi") {
                return {
                    debt: 1,
                    existingProperty: 1,
                    existingLoan: 1,
                    refiLoan: 1,
                };
            }
            if (type === "purchase") {
                return {
                    debt: 1,
                    property: 1,
                    purchaseLoan: 1,
                };
            }
            return {
                debt: 1,
                property: 2,
                existingProperty: 1,
                existingLoan: 1,
                purchaseLoan: 1,
            };
        },
        requiredAddon: EAddonType.LOAN_ADVISOR,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.LOAN_ADVISOR],
        pages: { results: true, review: true },
    },
    {
        name: "Real Estate Report Card",
        tool: "rerc",
        toolKey: "real_estate_report_card",
        version: "1.1",
        url: "/members/rerc",
        thumbnail: "https://mbshighway.com/s3/images/report-demos/real-estate-report-card.jpg",
        description:
            "Showcase appreciation rates, current renter data, supply and demand, median home price, median income, affordability, and more to illustrate the opportunity in homeownership.",
        categories: ["purchase", "invest"],
        category: "real-estate",
        customMessages: ({ address }) => ({
            default: `I wanted to send over this real estate report card for ${address}, which breaks down historical and estimated appreciation, housing availability, and demographic information for the area. Let me know if you have any questions or would like to discuss further.`,
            video: `I made a quick video explaining the details of the real estate report card for ${address}, including historical and estimated appreciation, housing availability, and demographic information for the area. Let me know if you have any questions or would like to discuss further.`,
        }),
        eventPrefix: "rerc",
        pages: { results: true, review: false },
    },
    {
        name: "Bid Over Ask",
        tool: "boa",
        toolKey: "bid_over_ask",
        version: "1.4",
        url: "/members/bid-over-ask",
        thumbnail: "https://mbshighway.com/s3/images/report-demos/bid-over-ask.jpg",
        description:
            "Use MBS Highway's historical and forecasted appreciation models to see when property appreciation will cover the additional bid over the asking price.",
        categories: ["purchase"],
        category: "real-estate",
        customMessages: () => ({
            default:
                "I wanted to send over your bid over ask scenario, which uses forecasted and historical appreciation models to identify when property appreciation will cover your additional bid over the asking price. This way, you'll know exactly how long it'll take to recoup the extra cost. Let me know if you have any questions — I'm here to chat any time.",
            video: "I made a quick video walking you through your bid over ask scenario. This tool uses forecasted and historical appreciation models to identify when property appreciation will cover your additional bid over the asking price. This way, you'll know exactly how long it'll take to recoup the extra cost. Let me know if you have any questions — I'm here to chat any time.",
        }),
        eventPrefix: "boa",
        requiredAddon: EAddonType.LOAN_ADVISOR,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.LOAN_ADVISOR],
        pages: { results: true, review: true },
    },
    {
        name: "Buy vs Rent",
        tool: "bvr",
        toolKey: "buy_vs_rent",
        version: "1.8",
        url: "/members/buy-vs-rent",
        thumbnail: "https://mbshighway.com/s3/images/report-demos/buy-vs-rent.png",
        description:
            "Quantify whether it's better to buy or rent by considering monthly payment, an estimate of the home's future value, and the wealth creation opportunity.",
        categories: ["purchase"],
        category: "real-estate",
        customMessages: () => ({
            default:
                "I created a buy versus rent report to help you compare estimated monthly payments for renting or buying and additional analytics. You'll be able to see an estimate of the home's future value, as well as the possible tax savings you could benefit from when buying a home. Let me know if you have any questions — I'm here to chat any time.",
            video: "I made a quick video walking you through a buy versus rent report to help you compare estimated monthly payments for renting or buying and additional analytics. You'll be able to see an estimate of the home's future value, as well as the possible tax savings you could benefit from when buying a home. Let me know if you have any questions — I'm here to chat any time.",
        }),
        eventPrefix: "bvr",
        pages: { results: true, review: true },
    },
    {
        name: "Appreciation",
        tool: "appr",
        toolKey: "appreciation",
        version: "1.9",
        url: "/members/appreciation",
        thumbnail: "https://mbshighway.com/s3/images/report-demos/appreciation.png",
        description:
            "Estimate future home value and return on investment by using forecasted, historical, and/or custom home price appreciation rates.",
        categories: ["purchase"],
        category: "real-estate",
        customMessages: () => ({
            default:
                "I've prepared an appreciation report that shows you how your home will appreciate over time and help build your net worth. Let me know if you have any questions — I'm here to chat any time.",
            video: "I made a quick video walking you through an appreciation calculator that shows you how your home will appreciate over time and help build your net worth. Let me know if you have any questions — I'm here to chat any time.",
        }),
        eventPrefix: "appreciation_calc",
        pages: { results: true, review: true },
    },
    {
        name: "Blended Rate Calculator",
        tool: "blend",
        toolKey: "blended_rate",
        version: "1.2",
        url: "/members/blended-rate",
        thumbnail: "https://mbshighway.com/s3/images/report-demos/blended-rate.png",
        description:
            "Give your clients a more accurate and correct rate when they consider all of their varying debts and related interest rates.",
        categories: ["refinance"],
        category: "calculators",
        customMessages: () => ({
            default:
                "I wanted to send over this blended rate loan calculator report, which gives you a more accurate and correct rate when you're using multiple loans to finance your home purchase. Let me know if you have any questions — I'm here to chat anytime.",
            video: "I made a quick video walking you through a blended rate loan calculator. This tool gives you a more accurate and correct rate when you're using multiple loans to finance your home purchase. Let me know if you have any questions — I'm here to chat anytime.",
        }),
        eventPrefix: "blended_rate",
        pages: { results: false, review: false },
    },
    {
        name: "Amortization Calculator",
        tool: "amort",
        toolKey: "amortization",
        version: "1.6",
        url: "/members/amortization",
        thumbnail: ["", "-2", "-3", "-4"].map(
            (n) => `https://mbshighway.com/s3/images/report-demos/amortization${n}.png`,
        ),
        description:
            "Provide a schedule of loan repayments, showing how each payment is split between principal and interest, and how interest savings increase over time with additional principal applied.",
        categories: ["purchase", "refinance"],
        category: "calculators",
        customMessages: () => ({
            default:
                "I wanted to send over a customized amortization report, which shows you a breakdown of your loan repayments over time. The calculator takes your loan amount, number of payments and interest rate to determine how much of each repayment will apply towards the principal and how much will apply towards interest. Let me know if you have any questions — I'm here to chat any time.",
            video: "I made a quick video walking you through your customized amortization report, which shows you a breakdown of your loan repayments over time. The calculator takes your loan amount, number of payments and interest rate to determine how much of each repayment will apply towards the principal and how much will apply towards interest. Let me know if you have any questions — I'm here to chat any time.",
        }),
        eventPrefix: "amortization",
        pages: { results: true, review: true },
    },
    {
        name: "Highway Record",
        tool: "record",
        toolKey: "recordings",
        version: "1.2",
        url: "/members/recordings",
        customMessages: () => ({
            default: "",
            video: "I wanted to send over a quick video - I hope you find it helpful. Let me know if you have any questions or would like to chat more to ensure we are aligned on what works best for you.",
        }),
        eventPrefix: "highway_record",
        requiredAddon: EAddonType.SCREEN_RECORDING,
    },
    {
        name: "Highway Meeting",
        tool: "meetings",
        toolKey: "meetings",
        version: "1.2",
        url: "/members/meetings",
        eventPrefix: "highway_meeting",
        requiredAddon: EAddonType.SCREEN_RECORDING,
    },
    {
        name: "Markets Overview",
        tool: "mrkt",
        toolKey: "markets",
        version: "1.3",
        url: "/members/markets",
        category: "markets",
        customMessages: () => ({
            default:
                "I wanted to send over a Markets Overview PDF, which should help you understand how the latest moves impact you now and in the future. Please let me know if you have any questions - I'm happy to set up a time to chat whenever it's convenient.",
            video: "I made you a quick video walking through a market overview, which should help you understand how the latest moves impact you now and in the future. Please let me know if you have any questions - I'm happy to set up a time to chat whenever it's convenient.",
        }),
        eventPrefix: "markets_overview",
    },
    {
        name: "Homepage",
        tool: "home",
        toolKey: "homepage",
        url: "/members/homepage",
        eventPrefix: "home",
    },
    {
        name: "House Price Index",
        tool: "hpi",
        toolKey: "house_price_index",
        version: "2.0",
        url: "/members/house-price-index",
        thumbnail: "https://mbshighway.com/s3/images/report-demos/house-price-index.png",
        description:
            "Display changes in single-family home values based on transaction data of repeat sales.",
        categories: ["invest"],
        eventPrefix: "hpi",
    },
    {
        name: "Weekly Newsletter",
        tool: "news",
        toolKey: "newsletter",
        version: "999.1",
        url: "/members/newsletter",
        eventPrefix: "weekly_newsletter",
    },
    {
        name: "Payment Calculator",
        tool: "pay",
        toolKey: "payment",
        version: "999.1",
        url: "/members/payment",
        eventPrefix: "payment_calc",
    },
    {
        name: "Refi Risk of Waiting Calculator",
        tool: "rce",
        toolKey: "refi_cow_express",
        version: "1.0",
        url: "/members/refi-cow-express",
        eventPrefix: "row",
        requiredAddon: EAddonType.LOAN_ADVISOR,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.LOAN_ADVISOR],
        pages: { results: true, review: false },
    },
    {
        name: "Reinvestment Calculator",
        tool: "reinvest",
        toolKey: "reinvestment",
        url: "/members/reinvestment",
        customMessages: () => ({
            default:
                "I wanted to send over a personalized Investment Calculator to help you understand how an investment property may positively impact your net worth and your finances. Please let me know if you have any questions - I'm happy to set up a time to chat whenever it is convenient.",
            video: "I made you a quick video walking through a personalized Investment Calculator to help you understand how an investment property may positively impact your net worth and your finances. Please let me know if you have any questions - I'm happy to set up a time to chat whenever it is convenient.",
        }),
        eventPrefix: "reinvestment_calc",
        pages: { results: true, review: false },
    },
    {
        name: "Equity Gained & Appreciation",
        tool: "eg",
        toolKey: "equity_gained",
        version: "1.0",
        url: "/members/equity-gained",
        thumbnail: "https://mbshighway.com/s3/images/report-demos/equity-gained.png",
        description:
            "Estimate potential wealth gains by combining initial down payment, forecasted appreciation, and amortization for up to a 15-year period.",
        categories: ["purchase"],
        category: "loan-advisor",
        customMessages: () => ({
            default:
                "I wanted to send over an Equity Gained report, which should help you understand the unique financial opportunity in housing and real estate today, based on forecasted and historical appreciation. Please let me know if you have any questions - I'm happy to set up a time to chat whenever it's convenient.",
            video: "I made you a quick video walking through an Equity Gained report, which should help you understand the unique financial opportunity in housing and real estate today, based on forecasted and historical appreciation. Please let me know if you have any questions - I'm happy to set up a time to chat whenever it's convenient.",
        }),
        eventPrefix: "equity_gained",
        requiredAddon: EAddonType.LOAN_ADVISOR,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.LOAN_ADVISOR],
        pages: { results: true, review: true },
    },
    {
        name: "Social Share",
        tool: "ssh",
        toolKey: "social_share",
        version: "1.0",
        url: "/members/social-share",
        eventPrefix: "social_share",
    },
    {
        name: "Social Studio",
        tool: "sst",
        toolKey: "social_studio",
        version: "1.0",
        url: "/members/social-studio",
        customMessages: () => ({
            default: "",
            video: "Watch the cool video now!",
        }),
        eventPrefix: "social_studio",
        requiredAddon: EAddonType.SOCIAL_STUDIO,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.SOCIAL_STUDIO],
    },
    {
        name: "Prequalified Buyer Campaign",
        tool: "mkit",
        version: "1.0",
        toolKey: "marketing_kit",
        url: "/members/marketing-kit",
        category: "marketing",
        eventPrefix: "marketing_kits",
        categories: ["purchase", "invest"],
        thumbnail: "https://mbshighway.com/s3/images/report-demos/marketing-kit.jpg",
        description:
            "Choose up to nine location-specific reports to highlight the benefits of homeownership and keep clients engaged throughout the homebuying process with a scheduled drip campaign.",
        requiredFields: () => ({
            attachedReports: 3,
            campaignSettings: 1,
        }),
        requiredAddon: EAddonType.MARKETING_KIT,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.MARKETING_KIT],
        pages: { results: true, review: false },
    },
    {
        name: "Formations and Completions",
        tool: "housing",
        toolKey: "formations_and_completions",
        version: "1.0",
        url: "/members/marketing-kit/formations-and-completions",
    },
    {
        name: "Home Values",
        tool: "impact",
        toolKey: "home_values",
        version: "1.0",
        url: "/members/marketing-kit/case-shiller",
    },
    {
        name: "Market Wrap",
        tool: "mrktw",
        toolKey: "market_wrap",
        url: "/members/market-wrap",
        eventPrefix: "market_wrap",
    },
    {
        name: "Morning Update",
        tool: "mu",
        toolKey: "morning_update",
        url: "/members/morning-update",
        eventPrefix: "morning_update",
    },
    {
        name: "A Week on the Highway",
        tool: "woh",
        toolKey: "week_on_highway",
        url: "/members/week-on-highway",
        eventPrefix: "week_on_highway",
    },
    {
        name: "Buying Power Calculator",
        tool: "bpow",
        toolKey: "buying_power",
        version: "1.0",
        url: "/members/buying-power",
        thumbnail: "https://mbshighway.com/s3/images/report-demos/buying-power.png",
        description:
            "Illustrate the change in loan amount needed to maintain a constant monthly payment at different interest rates. The tool also shows the change in payment for a constant loan amount at different interest rates.",
        categories: ["purchase"],
        customMessages: () => ({
            default:
                "I wanted to send over a Buying Power report, which helps to demonstrate your purchasing options and the impact of different interest rates on your monthly payments. Please let me know if you have any questions - I'm happy to set up a time to chat whenever it's convenient.",
            video: "I made you a quick video walking through a Buying Power report, to help demonstrate your purchasing options and the impact of different interest rates on your monthly payments. Please let me know if you have any questions - I'm happy to set up a time to chat whenever it's convenient.",
        }),
        eventPrefix: "buying_power",
        requiredAddon: EAddonType.MARKETING_KIT,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.MARKETING_KIT],
        pages: { results: false, review: false },
    },
    {
        name: "Presentation Expressway",
        tool: "presentation",
        toolKey: "presentation_expressway",
        version: "1.0",
        url: "/members/presentation-expressway",
        eventPrefix: "pe",
        requiredAddon: EAddonType.PRESENTATION_EXPRESSWAY,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.PRESENTATION_EXPRESSWAY],
    },
    {
        name: "Economic Calendar",
        tool: "econcal",
        toolKey: "econ_calendar",
        version: "1.0",
        url: "/members/econ-calendar",
        eventPrefix: "econ_calendar",
    },
    {
        name: "Mauldin: Thoughts from the Frontline",
        tool: "tftf",
        toolKey: "mauldin_tftf",
        version: "1.0",
        url: "/members/mauldin-tftf",
        eventPrefix: "mauldin",
    },
    {
        name: "Barry's Insights",
        tool: "bi",
        toolKey: "barrys_insights",
        version: "1.0",
        url: "/members/barrys-insights",
        eventPrefix: "barrys_insights",
    },
    {
        name: "Strike Rate",
        tool: "str",
        toolKey: "strike_rate",
        version: "1.0",
        url: "/members/strike-rate",
        eventPrefix: "strike_rate",
        requiredAddon: EAddonType.LOAN_ADVISOR,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.LOAN_ADVISOR],
    },
    {
        name: "ARM vs Fixed Calculator",
        tool: "avf",
        toolKey: "arm_vs_fixed",
        version: "1.0",
        url: "/members/arm-vs-fixed",
        thumbnail: "https://mbshighway.com/s3/images/report-demos/arm-vs-fixed.png",
        description:
            "Compare an adjustable rate mortgage (ARM) and fixed rate mortgage to show clients what would be the best option for them.",
        categories: ["purchase"],
        category: "calculators",
        customMessages: () => ({
            default:
                "I wanted to send over an ARM vs. Fixed report, which compares an adjustable rate mortgage (ARM) and fixed loan to see which one would be the best fit for you. Please let me know if you have any questions - I'm happy to set up a time to chat whenever it's convenient.",
            video: "I made you a quick video comparing an adjustable rate mortgage (ARM) and fixed loan to see which one would be the best fit for you.  Please let me know if you have any questions - I'm happy to set up a time to chat whenever it's convenient.",
        }),
        eventPrefix: "arm_vs_fixed",
        pages: { results: true, review: true },
    },
    {
        name: "Account Settings",
        tool: "accs",
        toolKey: "account_settings",
        version: "1.0",
        url: "/members/account-settings",
        category: "user",
        eventPrefix: "account_settings",
        notFavoritable: true,
    },
    {
        name: "Seller Contribution",
        tool: "sc",
        toolKey: "seller_contribution",
        url: "/members/seller-contribution",
        legacyUrl: "/members/advisor/seller-contribution",
        categories: ["purchase"],
        eventPrefix: "seller_contribution",
        requiredAddon: EAddonType.LOAN_ADVISOR,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.LOAN_ADVISOR],
    },
    {
        name: "Investment Property Analyzer",
        tool: "ipa",
        toolKey: "investment_property_analyzer",
        url: "/members/investment-property-analyzer",
        description: "See the potential returns an investment property could bring you.",
        categories: ["purchase", "invest"],
        thumbnail: [1, 2, 3].map((n) => `https://mbshighway.com/s3/images/report-demos/ipa-${n}.png`),
        featureFlag: "ipa",
        eventPrefix: "investment_property_analyzer",
        requiredAddon: EAddonType.LOAN_ADVISOR,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.LOAN_ADVISOR],
    },
    {
        name: "Open House Flyers",
        tool: "ohf",
        toolKey: "open_house_flyers",
        url: "/members/open-house-flyers",
        legacyUrl: "/members/marketing/open-house-flyers",
        eventPrefix: "open_house_flyers",
        requiredAddon: EAddonType.OPEN_HOUSE_KIT,
    },
    {
        name: "Affordability Calculator",
        tool: "aff",
        toolKey: "affordability",
        version: "1.0",
        url: "/members/affordability",
        category: "calculators",
        categories: ["purchase"],
        thumbnail: "https://mbshighway.com/s3/images/report-demos/affordability.png",
        description: "Use our affordability calculator to determine how much home you can afford.",
        customMessages: () => ({
            default:
                "I've prepared an affordability report that shows how much home you can afford based on your financial situation. Let me know if you have any questions — I'm here to chat any time.",
            video: "I made a quick video walking you through an affordability analysis that shows how much home you can afford. Let me know if you have any questions — I'm here to chat any time.",
        }),
        eventPrefix: "affordability_calc",
    },
    {
        name: "MBEstimate",
        tool: "mbe",
        toolKey: "mbestimate",
        url: "/members/mbestimate",
        description:
            "Provide clients with an estimate of their home's current market value using MBS Highway's proprietary valuation model.",
        requiredAddon: EAddonType.LOAN_ADVISOR,
        addonPageUrl: ADDON_PAGE_URLS[EAddonType.LOAN_ADVISOR],
    },
    {
        name: "Agent Feed",
        tool: "agent_feed",
        toolKey: "agent_feed",
        url: "/members/agent-feed",
        legacyUrl: "/members/agent-intel",
        eventPrefix: "agent_feed",
    },
    {
        name: "AVM Widget",
        tool: "avm",
        toolKey: "avm_widget",
        url: "/members/avm-widget",
        legacyUrl: "/members/real-estate/widgets",
        eventPrefix: "avm_widget",
    },
];

export const getToolName = (toolKey: TToolKeysWithVariants | TTool) => {
    // TODO deprecated TTool and only use toolKey
    const tool = // @ts-ignore
        (TOOLS_KEYS_WITH_VARIANTS.includes(toolKey) ? TOOL_MAP[toolKey] : TOOL_DICT[toolKey]) as ITool;
    if (!tool) return "";

    const isVariant = TOOL_VARIANT_KEYS.includes(toolKey as any); // no idea why this doesn't work...
    if (!isVariant) return tool.name;

    const variantLabel = tool?.variants?.find((v) => v.variantKey === toolKey)?.label;
    return `${tool.name} - ${variantLabel}`;
};

export const getTool = (tool: TTool) => {
    return TOOLS.find((_tool) => _tool.tool === tool);
};

export const getToolByName = (toolKey: TToolKey) => {
    return TOOLS.find((_tool) => _tool.toolKey === toolKey);
};

type TToolObj = { [key in TTool]: ITool };
export const TOOL_DICT = (() => {
    const obj: Partial<TToolObj> = {};

    TOOLS.forEach((t) => {
        obj[t.tool] = t;
    });

    return obj as TToolObj;
})();

type TToolKeyObj = { [key in TToolKeysWithVariants]: ITool };
export const TOOL_MAP = (() => {
    const obj: Partial<TToolKeyObj> = {};

    TOOLS.forEach((t) => {
        if (t.toolKey) obj[t.toolKey] = t;
        if (t.variants) {
            t.variants.forEach((tv) => (obj[tv.variantKey] = t));
        }
    });

    return obj as TToolKeyObj;
})();

export const getToolUrl = (tool: TTool) => {
    return TOOL_DICT[tool]["url"];
};

export const getFullToolUrl = (tool: TTool) => {
    return `${getHost()}${NEXT_PUBLIC_URL}${TOOL_DICT[tool]["url"]}`.replace(/(?<!:)\/\//g, "/");
};

export const getLiveShareUrl = (tool: TTool) => {
    return getFullToolUrl(tool)?.replace("/members/", "/live-share/");
};

/**
 * The landing page a sample-report requester is emailed (MIG-8637). Not a live-share URL: the
 * page gates the report behind cell + NMLS before it renders anything.
 */
export const getSampleLandingUrl = (sampleId: string) => {
    return `${getHost()}${NEXT_PUBLIC_URL}sample/${sampleId}`.replace(/(?<!:)\/\//g, "/");
};

export const getToolExportUrl = (tool: TTool) => {
    return getFullToolUrl(tool)?.replace("/members/", "/exports/");
};

export const getToolExportAuthUrl = (tool: TTool) => {
    return `${NEXT_PUBLIC_URL}api/v1/report/export-auth?tool=${tool}`;
};

/** Sibling of {@link getToolExportAuthUrl} for sample reports, which have no report id or LO. */
export const getSampleExportAuthUrl = (tool: TTool, sampleId: string) => {
    return `${NEXT_PUBLIC_URL}api/v1/report/sample-export-auth?tool=${tool}&sampleId=${sampleId}`;
};

export const buildToolUrl = (toolKey: TToolKey, path?: string) => {
    const base = TOOL_MAP[toolKey].url;
    if (!path) return base;
    return path.startsWith("?") ? `${base}${path}` : `${base}/${path}`;
};

/**
 * Returns the toolKey or variantKey for a given TTool + optional IToolMeta.type.
 * Use this when you have the raw tool identifier and report type (e.g. from a loaded report).
 * Note: IToolMeta.type uses "refinance" for Loan Compare Refi, but the variant pathSuffix uses "refi".
 * This function normalizes that inconsistency.
 */
export const getToolKeyOrVariantKey = (tool: TTool, type?: string): TToolKeysWithVariants | "" => {
    const toolInfo = TOOL_DICT[tool];
    if (!toolInfo?.toolKey) return "";
    if (toolInfo.variants?.length && type) {
        const normalizedType = type === "refinance" ? "refi" : type;
        const variant = toolInfo.variants.find((v) => v.pathSuffix?.includes(`type=${normalizedType}`));
        if (variant) return variant.variantKey;
    }
    return toolInfo.toolKey as TToolKeysWithVariants;
};

/** AI chat app names (hwy-ai-mcp tool configs, socket appName). Chat tokens may also write Portal. */
export const CHAT_APP_NAMES = ["mbs-bot", "agent-intel"] as const;
export type TChatAppName = (typeof CHAT_APP_NAMES)[number];

/** Socket appName of the mbs-api markets gateway. */
export const MARKETS_SOCKET_APP_NAME = "mbs-markets";

/**
 * Client token managers treat a highway/resource token as expired this long before its exp
 * (clock skew, in-flight latency). The mint route hands out tokens that outlive it.
 */
export const HIGHWAY_TOKEN_EXPIRY_MARGIN_MS = 5 * 60 * 1000;
